import React, { useState, useEffect, useMemo } from "react";
import { I } from "../data";
import { http, formatApiError } from "../api";
import { useConfirm } from "../components/ConfirmDialog";

const POLICY_BULLETS = [
  ["What we collect", "Only structured application details you re-type across portals (name, contact, address, family, funding, education, tests). Never essays, statements, or anything a university expects you to write yourself."],
  ["Why", "So the Vidhya browser extension can autofill university application forms for you — and nothing else."],
  ["Where", "Encrypted at rest in a store isolated from your AI advisor data. The extension keeps it in memory only and locks when you close your browser."],
  ["Sensitive IDs", "We never store passport numbers, national IDs/SSN, or proof-of-funds figures — you enter those directly on the portal."],
  ["Your control", "View, edit, or delete any field anytime. Withdraw consent to erase everything instantly."],
];

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
  return a;
}

function Field({ def, value, onChange }) {
  const id = `af-${def.key}`;
  if (def.type === "bool") {
    return (
      <div className="af-field">
        <label className="p-label" htmlFor={id}>{def.label}</label>
        <select
          id={id} className="input" data-testid={`autofill-field-${def.key}`}
          value={value === true ? "yes" : value === false ? "no" : ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : e.target.value === "yes")}
        >
          <option value="">—</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>
    );
  }
  return (
    <div className="af-field">
      <label className="p-label" htmlFor={id}>{def.label}</label>
      <input
        id={id} className="input" data-testid={`autofill-field-${def.key}`}
        type={def.type === "date" ? "date" : def.type === "email" ? "email" : def.type === "tel" ? "tel" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export default function Autofill() {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [consent, setConsent] = useState({ status: "none" });
  const [version, setVersion] = useState(0);
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [guardian, setGuardian] = useState({ guardian_name: "", guardian_email: "" });

  const granted = consent?.status === "granted";
  const age = ageFromDob(draft.dob);
  const isMinor = age !== null && age < 18;
  const guardianMissing = isMinor && !(consent?.guardian_email);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        http.get("/autofill/schema"),
        http.get("/autofill/profile"),
      ]);
      setSchema(s.data);
      setConsent(p.data.consent || { status: "none" });
      setVersion(p.data.version || 0);
      setDraft(p.data.fields || {});
      const first = (s.data.categories || [])[0];
      if (first) setOpen({ [first.key]: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const grant = async () => {
    setGranting(true); setErr("");
    try {
      const body = isMinor ? { guardian_name: guardian.guardian_name || null, guardian_email: guardian.guardian_email || null } : {};
      const r = await http.post("/autofill/consent", body);
      setConsent(r.data.consent);
      setVersion(r.data.version || 1);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setGranting(false); }
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const r = await http.put("/autofill/profile", { fields: draft });
      setVersion(r.data.version);
      setDraft(r.data.fields || {});
      setSavedAt(Date.now());
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveGuardian = async () => {
    setErr("");
    try {
      const r = await http.post("/autofill/consent", { guardian_name: guardian.guardian_name || null, guardian_email: guardian.guardian_email || null });
      setConsent(r.data.consent);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const withdraw = async () => {
    const ok = await confirm({
      title: "Withdraw consent & erase data?",
      message: "This permanently deletes every application detail you've saved. The extension will no longer be able to fill forms. You can re-consent later.",
      confirmLabel: "Erase everything", danger: true,
    });
    if (!ok) return;
    try {
      await http.delete("/autofill/consent");
      setConsent({ status: "withdrawn" });
      setDraft({}); setVersion((v) => v + 1);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const filledCount = useMemo(() => Object.values(draft).filter((v) => v !== "" && v != null).length, [draft]);

  if (loading) return <div className="full-loader" data-testid="autofill-loading">Loading…</div>;

  return (
    <div data-testid="autofill-view">
      <div className="dash-hero">
        <div>
          <div className="eyebrow">Apply faster · Autofill</div>
          <h1 style={{ marginTop: 8, fontSize: 32 }}>Application <em>autofill</em></h1>
          <p>Save the details you re-type on every portal once. The Vidhya browser extension fills them for you — you stay in control and we never touch your essays.</p>
        </div>
      </div>

      {err && <div className="auth-error" data-testid="autofill-error">{err}</div>}

      {!granted ? (
        <div className="profile-section" data-testid="autofill-consent-gate" style={{ maxWidth: 680 }}>
          <div className="p-section-head">
            <div className="eyebrow">Before we collect anything</div>
            <h3 className="p-section-title">Your consent, <em>your control</em></h3>
          </div>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 4 }}>
            We only collect these details when you choose to start applying — never before. Here's exactly what you're agreeing to:
          </p>
          <div style={{ display: "grid", gap: 10, margin: "14px 0" }}>
            {POLICY_BULLETS.map(([h, b]) => (
              <div key={h} style={{ display: "flex", gap: 10 }}>
                <span style={{ marginTop: 2, color: "var(--accent, #B85A3D)" }}><I.Check size={15} /></span>
                <div><strong style={{ fontSize: 13.5 }}>{h}.</strong> <span className="muted" style={{ fontSize: 13 }}>{b}</span></div>
              </div>
            ))}
          </div>

          {isMinor && (
            <div className="af-minor" data-testid="autofill-minor-consent" style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: 14, margin: "10px 0" }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 6 }}>You're under 18 — a parent/guardian must consent</div>
              <p className="muted" style={{ fontSize: 12.5, margin: "0 0 10px" }}>Required by data-protection law (GDPR / India DPDP). Add your guardian's details to continue.</p>
              <div className="af-grid">
                <Field def={{ key: "guardian_name", label: "Guardian name", type: "text" }} value={guardian.guardian_name} onChange={(v) => setGuardian((g) => ({ ...g, guardian_name: v }))} />
                <Field def={{ key: "guardian_email", label: "Guardian email", type: "email" }} value={guardian.guardian_email} onChange={(v) => setGuardian((g) => ({ ...g, guardian_email: v }))} />
              </div>
            </div>
          )}

          <p className="muted" style={{ fontSize: 11.5, margin: "6px 0 14px" }}>
            See our <a href="/privacy" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>privacy policy</a>. This data is used only for autofill and never sold or shared.
          </p>
          <button className="btn-primary" data-testid="autofill-consent-btn" onClick={grant}
            disabled={granting || (isMinor && (!guardian.guardian_name || !guardian.guardian_email))}>
            {granting ? "Saving…" : "I consent — start filling my details"}
          </button>
          {consent?.status === "withdrawn" && (
            <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>Your previous data was erased. Consent again to start fresh.</div>
          )}
        </div>
      ) : (
        <>
          <div className="af-toolbar" data-testid="autofill-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div className="muted" style={{ fontSize: 13 }}>
              <strong>{filledCount}</strong> details saved · profile v{version}
              {savedAt && <span style={{ color: "#16a34a", marginLeft: 8 }}>Saved ✓</span>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-ghost" data-testid="autofill-withdraw-btn" onClick={withdraw} style={{ color: "#dc2626" }}>
                <I.Trash size={13} /> Withdraw & erase
              </button>
              <button className="btn-primary" data-testid="autofill-save-btn" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>

          {guardianMissing && (
            <div className="profile-section" data-testid="autofill-guardian-prompt" style={{ background: "#FFF7ED", border: "1px solid #FED7AA", marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 6 }}>Guardian consent needed</div>
              <p className="muted" style={{ fontSize: 12.5, margin: "0 0 10px" }}>Your date of birth indicates you're under 18. Add a parent/guardian to keep saving.</p>
              <div className="af-grid">
                <Field def={{ key: "guardian_name", label: "Guardian name", type: "text" }} value={guardian.guardian_name} onChange={(v) => setGuardian((g) => ({ ...g, guardian_name: v }))} />
                <Field def={{ key: "guardian_email", label: "Guardian email", type: "email" }} value={guardian.guardian_email} onChange={(v) => setGuardian((g) => ({ ...g, guardian_email: v }))} />
              </div>
              <button className="btn-primary" data-testid="autofill-guardian-save-btn" style={{ marginTop: 10 }} onClick={saveGuardian}>Save guardian consent</button>
            </div>
          )}

          {(schema?.categories || []).map((cat) => {
            const isOpen = !!open[cat.key];
            const catFilled = cat.fields.filter((f) => draft[f.key] !== "" && draft[f.key] != null).length;
            return (
              <div className="profile-section af-cat" key={cat.key} data-testid={`autofill-cat-${cat.key}`} style={{ marginBottom: 12 }}>
                <div className="af-cat-head" onClick={() => setOpen((o) => ({ ...o, [cat.key]: !o[cat.key] }))}
                  data-testid={`autofill-cat-toggle-${cat.key}`}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {cat.sensitive && <span title="Sensitive — strongest protection" style={{ color: "#B85A3D" }}><I.Shield size={14} /></span>}
                    <h3 className="p-section-title" style={{ margin: 0, fontSize: 16 }}>{cat.label}</h3>
                    <span className="muted" style={{ fontSize: 12 }}>{catFilled}/{cat.fields.length}</span>
                  </div>
                  <span style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}><I.Caret size={14} /></span>
                </div>
                {isOpen && (
                  <div className="af-grid" style={{ marginTop: 14 }}>
                    {cat.fields.map((f) => (
                      <Field key={f.key} def={f} value={draft[f.key]} onChange={(v) => set(f.key, v)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div className="profile-section" data-testid="autofill-extension-card" style={{ marginTop: 16 }}>
            <div className="eyebrow">Browser extension</div>
            <h3 className="p-section-title" style={{ fontSize: 16 }}>Use it on <em>university portals</em></h3>
            <p className="muted" style={{ fontSize: 13, lineHeight: 1.6, marginTop: 4 }}>
              Install the Vidhya Autofill extension, click "Connect", and sign in with this account. It syncs these details (read-only) and fills supported portals — it never submits for you and never touches essays.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
